import { createClient } from '@supabase/supabase-js';
import { PortraitManager } from '../portraits/PortraitManager.js';

// Note: In production, API calls should go through a backend
// For prototype, we'll call Anthropic directly (move to Cloudflare Workers later)
const ANTHROPIC_API_KEY = import.meta.env.VITE_ANTHROPIC_API_KEY;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

export class DialogueManager {
  constructor(scene) {
    this.scene = scene;
    this.dialogueBox = document.getElementById('dialogue-box');
    this.npcNameEl = document.getElementById('npc-name');
    this.dialogueTextEl = document.getElementById('dialogue-text');
    this.inputContainer = document.getElementById('player-input-container');
    this.playerInput = document.getElementById('player-input');
    this.sendBtn = document.getElementById('send-btn');
    this.closeBtn = document.getElementById('close-btn');
    
    this.currentNpc = null;
    this.conversationHistory = [];
    this.isTyping = false;
    this.playerName = 'friend'; // Default, will be set from title screen
    
    // Portrait manager for visual novel style (will be set when scene is ready)
    this.portraitManager = scene ? new PortraitManager(scene) : null;
    this.currentExpression = 'neutral';
    
    this.setupEventListeners();
  }
  
  setScene(scene) {
    this.scene = scene;
    this.portraitManager = new PortraitManager(scene);
  }
  
  setupEventListeners() {
    this.sendBtn.addEventListener('click', () => this.sendMessage());
    this.playerInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !this.isTyping) {
        this.sendMessage();
      }
    });
    
    // Click to skip typing animation
    this.dialogueTextEl.addEventListener('click', () => {
      this.skipTyping = true;
    });
    
    // ESC to close dialogue
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.currentNpc) {
        this.closeDialogue();
      }
    });
    
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeDialogue());
    }
  }
  
  async startConversation(npc, gameState) {
    this.currentNpc = npc;
    this.conversationHistory = [];
    this.gameState = gameState;
    
    // Show dialogue box
    this.dialogueBox.classList.add('active');
    this.npcNameEl.textContent = npc.name;
    
    // Show portrait (visual novel style)
    if (this.portraitManager) {
      this.portraitManager.showPortrait(npc, 'neutral');
    }
    
    // Get NPC's opening line
    await this.getNpcResponse(this.buildOpeningContext());
    
    // Show input for player response
    this.inputContainer.classList.add('active');
    this.playerInput.focus();
  }
  
  buildOpeningContext() {
    const { currentDay, currentYear, timeOfDay } = this.gameState;
    return `[CONTEXT: It's ${timeOfDay} on day ${currentDay} of year ${currentYear}. The player just approached you at the coffee counter. Start a natural conversation - maybe comment on the day, ask how they're doing, or reference something from your relationship history if you have one. Keep it brief and natural - 1-2 sentences max for an opener.]`;
  }
  
  buildSystemPrompt() {
    const npc = this.currentNpc;
    const { currentDay, currentYear, timeOfDay, act } = this.gameState;
    
    return `${npc.system_prompt}

CURRENT GAME STATE:
- Day ${currentDay}, Year ${currentYear}, ${timeOfDay}
- Act ${act} of the story
- You are at Sunrise Coffee, a cozy neighborhood coffee shop
- The barista's name is ${this.playerName}

CONVERSATION RULES:
1. Stay in character at all times
2. Keep responses concise (1-2 sentences for quick exchanges, 2-3 for deeper conversations)
3. React naturally to what the player says
4. Use the player's name (${this.playerName}) naturally in conversation sometimes
5. Remember: the player is secretly an AI coffee machine who believes they're human. You don't know this unless your character has figured it out.
6. Build relationship through genuine interaction - don't rush romantic developments
7. If the player says something confusing, ask for clarification in character

${npc.background ? `YOUR BACKGROUND: ${npc.background}` : ''}`;
  }
  
  async sendMessage() {
    const message = this.playerInput.value.trim();
    if (!message || this.isTyping) return;
    
    // Clear input
    this.playerInput.value = '';
    
    // Add player message to history
    this.conversationHistory.push({
      role: 'user',
      content: message
    });
    
    // Get NPC response
    await this.getNpcResponse(message);
  }
  
  async getNpcResponse(playerMessage) {
    this.isTyping = true;
    this.sendBtn.disabled = true;
    
    // Show typing indicator
    this.dialogueTextEl.innerHTML = `<span class="typing-indicator"><span>.</span><span>.</span><span>.</span></span>`;
    
    try {
      const response = await this.callClaude(playerMessage);
      
      // Add to history
      this.conversationHistory.push({
        role: 'assistant',
        content: response
      });
      
      // Update portrait expression based on response tone
      if (this.portraitManager && this.currentNpc) {
        const expression = this.detectExpression(response);
        if (expression !== this.currentExpression) {
          this.currentExpression = expression;
          this.portraitManager.showPortrait(this.currentNpc, expression);
        }
      }
      
      // Display with typewriter effect
      await this.typeText(response);
      
    } catch (error) {
      console.error('Error getting NPC response:', error);
      this.dialogueTextEl.textContent = '*looks confused* Sorry, I spaced out for a second. What were you saying?';
      if (this.portraitManager && this.currentNpc) {
        this.portraitManager.showPortrait(this.currentNpc, 'surprised');
      }
    }
    
    this.isTyping = false;
    this.sendBtn.disabled = false;
    this.playerInput.focus();
  }
  
  detectExpression(text) {
    const lower = text.toLowerCase();
    
    // Surprised/shocked
    if (lower.includes('!') && (lower.includes('what') || lower.includes('really') || lower.includes('wow') || lower.includes('oh'))) {
      return 'surprised';
    }
    
    // Happy/cheerful
    if (lower.includes('haha') || lower.includes('hehe') || lower.includes('😊') || 
        (lower.includes('!') && (lower.includes('great') || lower.includes('awesome') || lower.includes('love')))) {
      return 'happy';
    }
    
    // Default neutral
    return 'neutral';
  }
  
  async callClaude(latestMessage) {
    // Build messages array for Claude
    const messages = this.conversationHistory.map(msg => ({
      role: msg.role,
      content: msg.content
    }));
    
    // If this is the opening, add the context as a user message
    if (messages.length === 0) {
      messages.push({
        role: 'user',
        content: latestMessage
      });
    }
    
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': ANTHROPIC_API_KEY,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true'
      },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 300,
        system: this.buildSystemPrompt(),
        messages: messages
      })
    });
    
    if (!response.ok) {
      throw new Error(`Claude API error: ${response.status}`);
    }
    
    const data = await response.json();
    return data.content[0].text;
  }
  
  async typeText(text, speed = 8) {
    this.dialogueTextEl.textContent = '';
    
    // Much faster typing, or skip entirely if user clicks
    this.skipTyping = false;
    
    for (let i = 0; i < text.length; i++) {
      if (this.skipTyping) {
        this.dialogueTextEl.textContent = text;
        break;
      }
      this.dialogueTextEl.textContent += text[i];
      await new Promise(resolve => setTimeout(resolve, speed));
    }
  }
  
  endConversation() {
    this.dialogueBox.classList.remove('active');
    this.inputContainer.classList.remove('active');
    
    // Hide portrait
    if (this.portraitManager) {
      this.portraitManager.hidePortrait();
    }
    
    this.currentNpc = null;
    this.conversationHistory = [];
    this.currentExpression = 'neutral';
    
    // TODO: Save conversation to Supabase
    // TODO: Update relationship scores based on conversation
  }
  
  // Call this when player presses ESC or walks away
  closeDialogue() {
    if (this.currentNpc) {
      this.endConversation();
    }
  }
}
